import { ReactNode } from 'react';

import { ChevronDown, CircleHelp } from 'lucide-react';

import { CollapsibleTrigger } from 'components/Collapsible';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/Tooltip';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { cn } from 'utils/shadcn';

type CompletedGroupHeaderProps = {
  icon: ReactNode;
  label: string;
  count: number;
  explanation: string;
  // Border, background and hover classes for the header's state color
  colorClass: string;
};

// Header of a collapsed handled quote or built load group, e.g. "Quoted (8)" or "Built (2)",
// with a help icon that explains the state. Render it inside a Collapsible.
export function CompletedGroupHeader({
  icon,
  label,
  count,
  explanation,
  colorClass,
}: CompletedGroupHeaderProps) {
  return (
    <Flex
      align='center'
      className={cn('w-full rounded border transition-colors', colorClass)}
    >
      <CollapsibleTrigger className='group flex min-w-0 flex-1 items-center justify-between gap-1.5 border-none bg-transparent px-3 py-1.5 text-left focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-neutral-400'>
        <Flex align='center' gap='sm' className='min-w-0 flex-1'>
          {icon}
          <Typography variant='body-xs'>{`${label} (${count})`}</Typography>
        </Flex>
        <ChevronDown className='h-4 w-4 shrink-0 text-neutral-600 transition-transform group-data-[state=closed]:-rotate-90' />
      </CollapsibleTrigger>
      <Tooltip disableHoverableContent>
        <TooltipTrigger asChild>
          <button
            type='button'
            aria-label={`What does ${label} mean?`}
            className='mr-3 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-none bg-transparent p-0 text-neutral-600 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-neutral-400'
          >
            <CircleHelp className='h-3.5 w-3.5' strokeWidth={2} aria-hidden />
          </button>
        </TooltipTrigger>
        <TooltipContent className='pointer-events-none max-w-[240px]'>
          <Typography variant='body-xs'>{explanation}</Typography>
        </TooltipContent>
      </Tooltip>
    </Flex>
  );
}
